import React, { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { AxiosError } from 'axios';
import {
    fetchTimesheetsForApprovalAsync,
    approveTimesheetsAsync,
    rejectTimesheetAsync,
} from '../../../actions/TimesheetActions';
import type { TTimesheet, TApprovalErrorType } from '../../../types/timesheet';
import type { TApiResponse } from '../../../types/api';
import TimesheetList from './TimesheetList';
import ErrorPage from './ErrorPage';
import RefreshWarningModal from './RefreshWarningModal';
import './approval.style.scss';

type PageState = 'loading' | 'loaded' | 'error';

const REFRESH_REMINDER_TIME = 5 * 60 * 1000; // 5 minutes in milliseconds

const TimesheetApprovalPage: React.FC = () => {
    const dispatch = useDispatch();
    const location = useLocation();

    const [pageState, setPageState] = useState<PageState>('loading');
    const [timesheets, setTimesheets] = useState<TTimesheet[]>([]);
    const [clientName, setClientName] = useState<string>('');
    const [token, setToken] = useState<string>('');
    const [errorType, setErrorType] = useState<TApprovalErrorType>('general');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [notification, setNotification] = useState<{
        show: boolean;
        message: string;
        type: 'success' | 'error';
    }>({ show: false, message: '', type: 'success' });

    // Auto-refresh state
    const [showRefreshModal, setShowRefreshModal] = useState(false);
    const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);

    const showNotificationMessage = (message: string, type: 'success' | 'error') => {
        setNotification({ show: true, message, type });
        const timeout = type === 'success' ? 3000 : 5000;
        setTimeout(() => {
            setNotification({ show: false, message: '', type });
        }, timeout);
    };

    const handleError = (error: AxiosError<TApiResponse<unknown>>) => {
        if (error.response) {
            const { status, data } = error.response;
            const errorCode = data?.meta?.error_code;

            if (status === 401 || status === 403 || errorCode === 'ERR_TOKEN_INVALID') {
                setErrorType('invalid');
            } else if (status === 410 || errorCode === 'ERR_TOKEN_EXPIRED') {
                setErrorType('expired');
            } else {
                setErrorType('general');
                setErrorMessage(data?.meta?.detail || 'An error occurred');
            }
        } else if (error.message === 'Network Error') {
            setErrorType('network');
        } else {
            setErrorType('general');
            setErrorMessage(error.message || 'An error occurred');
        }
        setPageState('error');
    };

    const loadTimesheets = async(tokenParam: string) => {
        setPageState('loading');

        try {
            await dispatch(
                fetchTimesheetsForApprovalAsync(
                    tokenParam,
                    data => {
                        setTimesheets(data.timesheets || []);
                        setClientName(data.client_name || '');
                        setPageState('loaded');
                    },
                    error => {
                        handleError(error);
                    },
                ),
            );
        } catch (error) {
            handleError(error as AxiosError<TApiResponse<unknown>>);
        }
    };

    const clearRefreshTimer = () => {
        if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = null;
        }
    };

    const startRefreshTimer = () => {
        // Clear any existing timer
        clearRefreshTimer();

        // Start 5-minute timer
        refreshTimerRef.current = setTimeout(() => {
            // Only show modal if it's not already open
            setShowRefreshModal(prevState => {
                if (!prevState) {
                    return true;
                }
                return prevState;
            });
        }, REFRESH_REMINDER_TIME);
    };

    const handleDismissRefreshModal = () => {
        setShowRefreshModal(false);
        // Restart the 5-minute timer
        startRefreshTimer();
    };

    const handleRefreshNow = () => {
        window.location.reload();
    };

    // Extract token from URL and load timesheets on mount
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const tokenFromUrl = searchParams.get('token');

        if (tokenFromUrl) {
            setToken(tokenFromUrl);
            loadTimesheets(tokenFromUrl);
        } else {
            setErrorType('invalid');
            setPageState('error');
        }

        // Cleanup timer on unmount
        return () => {
            if (refreshTimerRef.current) {
                clearTimeout(refreshTimerRef.current);
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Start refresh timer when page is successfully loaded
    useEffect(() => {
        if (pageState === 'loaded') {
            startRefreshTimer();
        }
        return () => {
            clearRefreshTimer();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageState]);

    const handleApprove = async(timesheetIds: number[]) => {
        try {
            await dispatch(
                approveTimesheetsAsync(
                    token,
                    timesheetIds,
                    () => {
                        // Show success notification
                        const count = timesheetIds.length;
                        const message =
                            count === 1
                                ? 'Timesheet approved successfully'
                                : `${count} timesheets approved successfully`;
                        showNotificationMessage(message, 'success');

                        // Refetch timesheets to get latest data and any new timesheets
                        loadTimesheets(token);
                    },
                    () => {
                        showNotificationMessage('Failed to approve timesheets. Please try again.', 'error');
                    },
                ),
            );
        } catch (error) {
            showNotificationMessage('Failed to approve timesheets. Please try again.', 'error');
        }
    };

    const handleReject = async(timesheetId: number, reason: string) => {
        try {
            await dispatch(
                rejectTimesheetAsync(
                    token,
                    timesheetId,
                    reason,
                    () => {
                        showNotificationMessage('Timesheet rejected successfully', 'success');

                        // Refetch timesheets to get latest data and any new timesheets
                        loadTimesheets(token);
                    },
                    () => {
                        showNotificationMessage('Failed to reject timesheet. Please try again.', 'error');
                    },
                ),
            );
        } catch (error) {
            showNotificationMessage('Failed to reject timesheet. Please try again.', 'error');
        }
    };

    const handleRetry = () => {
        loadTimesheets(token);
    };

    const renderContent = () => {
        if (pageState === 'loading') {
            return (
                <div className="loading-container">
                    <div className="loading-spinner" />
                    <p>Loading timesheets...</p>
                </div>
            );
        }

        if (pageState === 'error') {
            return (
                <ErrorPage
                    errorType={errorType}
                    message={errorMessage}
                    onRetry={errorType === 'network' ? handleRetry : undefined}
                />
            );
        }

        return (
            <>
                <div className="approval-page__header">
                    <h1 className="approval-page__page-title">Timesheets Awaiting Approval</h1>
                    {clientName && (
                        <p className="approval-page__welcome-message">
                            Welcome, {clientName}
                        </p>
                    )}
                    <p className="approval-page__description">
                        Please review the timesheets below. You can approve individual timesheets by clicking the &quot;Approve&quot; button,
                        or select multiple timesheets using the checkboxes and approve them all at once using the &quot;Approve selected Timesheets&quot;
                        button at the bottom. If you need to reject a timesheet, please click &quot;Reject&quot; and provide a reason.
                    </p>
                </div>

                <div className="approval-page__table-container">
                    <TimesheetList
                        timesheets={timesheets}
                        onApprove={handleApprove}
                        onReject={handleReject}
                    />
                </div>
            </>
        );
    };

    return (
        <div className="operations-page console">
            {notification.show && (
                <div className={`approval-notification approval-notification--${notification.type}`}>
                    <span className="approval-notification__message">{notification.message}</span>
                    <button
                        type="button"
                        className="approval-notification__close"
                        onClick={() => setNotification({ show: false, message: '', type: 'success' })}
                    >
                        ×
                    </button>
                </div>
            )}

            <RefreshWarningModal
                isOpen={showRefreshModal}
                onDismiss={handleDismissRefreshModal}
                onRefresh={handleRefreshNow}
                reminderMinutes={REFRESH_REMINDER_TIME / 60 / 1000}
            />

            <div className="operations-page__inner">
                <div className="operations-page__row">
                    <div className="operations-page__aside-column">
                        <div className="operations-page__aside-content">
                            <div className="operations-page__logo">
                                <img
                                    alt="Logo white"
                                    src="/static/images/logo-white.png"
                                    width="130"
                                    height="40"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="operations-page__content-column">
                        {renderContent()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TimesheetApprovalPage;

